/**
 * VoxelGrid (Voxel-Skinner port) — the classified voxel grid the vendored
 * voxelizer fills, kept in its NATIVE index convention. ONE responsibility:
 * carry a uniform cubic-voxel grid over a bounding box, with per-voxel EMPTY /
 * BOUNDARY / INTERIOR state. This is a faithful TypeScript port of the accepted
 * zero-dependency Voxel-Skinner data structure — the algorithm is unchanged;
 * only types were added.
 *
 * VS-native memory layout: `index(x,y,z) = z*resY*resX + y*resX + x` (x-fastest,
 * z-slowest). This is NOT E1's `x + nx*(y + ny*z)` — the two are algebraically
 * equal for these dims, but the adapter (hands/voxelize.ts) still decodes here
 * and re-encodes through E1's `voxelIndex` so the bridge survives any future
 * divergence. The VoxelState constants stay INTERNAL to this voxel/ subdir
 * (ADR-010 §4): the kernel's public surface is the adapter, not the voxelizer.
 *
 * Depends on: nothing (zero-dep, engine-agnostic — no three.js / DOM / timers).
 * Depended on by: hands/voxel/voxelizer, and (through it) hands/voxelize.
 *
 * Credit: vendored port of `@p0qp0q/voxel-skinner` `src/VoxelGrid.js`
 * (Dionne & de Lasa geodesic voxel-binding lineage; SAT + flood-fill classifier
 * per its README, "Geodesic Voxel Binding for Production Character Meshing",
 * SCA 2013). Ported into the kernel workspace as the zero-dep library it is.
 */

/** Per-voxel classification. Internal to the voxel/ vendor subdir (ADR-010 §4). */
export const VoxelState = {
  EMPTY: 0,
  BOUNDARY: 1,
  INTERIOR: 2,
} as const;
export type VoxelStateValue = (typeof VoxelState)[keyof typeof VoxelState];

/** Axis-aligned bounding box the grid discretizes. */
export interface Bounds {
  readonly min: readonly [number, number, number];
  readonly max: readonly [number, number, number];
}

/** Counts per {@link VoxelState}, plus the grid total. */
export interface StateCounts {
  readonly empty: number;
  readonly boundary: number;
  readonly interior: number;
  readonly total: number;
}

/**
 * A uniform grid of cubic voxels over `bounds`, padded by one voxel on every
 * side (the padding shell is what the flood-fill seeds from). Scalars are fixed
 * at construction; only {@link state} contents mutate as the voxelizer classifies.
 */
export class VoxelGrid {
  readonly voxelSize: number;
  readonly resX: number;
  readonly resY: number;
  readonly resZ: number;
  readonly originX: number;
  readonly originY: number;
  readonly originZ: number;
  readonly totalVoxels: number;
  readonly state: Uint8Array;
  readonly bounds: Bounds;
  readonly resolution: number;

  /**
   * @param bounds     Axis-aligned bounding box `{ min:[x,y,z], max:[x,y,z] }`.
   * @param resolution Target voxels along the longest axis (default 64).
   */
  constructor(bounds: Bounds, resolution = 64) {
    const min = bounds.min;
    const max = bounds.max;

    // Extent per axis.
    const extentX = max[0] - min[0];
    const extentY = max[1] - min[1];
    const extentZ = max[2] - min[2];
    const maxExtent = Math.max(extentX, extentY, extentZ);

    // Cubic voxel size based on the longest axis.
    this.voxelSize = maxExtent / resolution;

    // Resolution per axis (at least 1)…
    this.resX = Math.max(1, Math.ceil(extentX / this.voxelSize));
    this.resY = Math.max(1, Math.ceil(extentY / this.voxelSize));
    this.resZ = Math.max(1, Math.ceil(extentZ / this.voxelSize));

    // …padded by one voxel on each side for the flood-fill boundary.
    this.resX += 2;
    this.resY += 2;
    this.resZ += 2;

    // Origin: min corner offset by one voxel of padding.
    this.originX = min[0] - this.voxelSize;
    this.originY = min[1] - this.voxelSize;
    this.originZ = min[2] - this.voxelSize;

    this.totalVoxels = this.resX * this.resY * this.resZ;

    // State grid: EMPTY / BOUNDARY / INTERIOR (zero-initialized = EMPTY).
    this.state = new Uint8Array(this.totalVoxels);

    this.bounds = { min: [min[0], min[1], min[2]], max: [max[0], max[1], max[2]] };
    this.resolution = resolution;
  }

  /** Flat index from 3D coordinates (VS-native `z*resY*resX + y*resX + x`). */
  index(x: number, y: number, z: number): number {
    return z * this.resY * this.resX + y * this.resX + x;
  }

  /** 3D coordinates from a flat index (inverse of {@link index}). */
  coords(idx: number): [number, number, number] {
    const x = idx % this.resX;
    const y = Math.floor(idx / this.resX) % this.resY;
    const z = Math.floor(idx / (this.resX * this.resY));
    return [x, y, z];
  }

  /** World-space position of a voxel's center. */
  worldPos(x: number, y: number, z: number): [number, number, number] {
    return [
      this.originX + (x + 0.5) * this.voxelSize,
      this.originY + (y + 0.5) * this.voxelSize,
      this.originZ + (z + 0.5) * this.voxelSize,
    ];
  }

  /** Voxel coordinates for a world position (clamped to the grid). */
  voxelCoords(wx: number, wy: number, wz: number): [number, number, number] {
    const x = Math.floor((wx - this.originX) / this.voxelSize);
    const y = Math.floor((wy - this.originY) / this.voxelSize);
    const z = Math.floor((wz - this.originZ) / this.voxelSize);
    return [
      Math.max(0, Math.min(this.resX - 1, x)),
      Math.max(0, Math.min(this.resY - 1, y)),
      Math.max(0, Math.min(this.resZ - 1, z)),
    ];
  }

  /** Whether integer coordinates lie inside the grid. */
  inBounds(x: number, y: number, z: number): boolean {
    return x >= 0 && x < this.resX && y >= 0 && y < this.resY && z >= 0 && z < this.resZ;
  }

  /** State of a voxel (in-range access; `!` mirrors the E1 kernel's typed-array style). */
  getState(x: number, y: number, z: number): number {
    return this.state[this.index(x, y, z)]!;
  }

  /** Set a voxel's state. */
  setState(x: number, y: number, z: number, val: number): void {
    this.state[this.index(x, y, z)] = val;
  }

  /** Tally voxels by state. */
  countByState(): StateCounts {
    let empty = 0;
    let boundary = 0;
    let interior = 0;
    for (let i = 0; i < this.totalVoxels; i += 1) {
      switch (this.state[i]) {
        case VoxelState.EMPTY:
          empty += 1;
          break;
        case VoxelState.BOUNDARY:
          boundary += 1;
          break;
        case VoxelState.INTERIOR:
          interior += 1;
          break;
        default:
          break;
      }
    }
    return { empty, boundary, interior, total: this.totalVoxels };
  }
}
